export const BILLING_DEFAULT_PAGE_SIZE = 50;

export interface BillingPaginationParams {
  page: number;
  pageSize: number;
}

export interface PaginatedResult<T> {
  items: T[];
  count: number;
  page: number;
  pageSize: number;
}

const LIST_KEYS = ["results", "data", "data_listing"] as const;

function asRecord(response: unknown): Record<string, unknown> | null {
  if (!response || typeof response !== "object" || Array.isArray(response)) {
    return null;
  }

  return response as Record<string, unknown>;
}

function parsePositiveNumber(value: unknown): number | null {
  if (value === undefined || value === null || value === "") {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}

export function extractPaginatedList(
  response: unknown,
  listKeys: readonly string[] = LIST_KEYS
): unknown[] {
  if (Array.isArray(response)) {
    return response;
  }

  const record = asRecord(response);
  if (!record) {
    return [];
  }

  for (const key of listKeys) {
    const list = record[key];
    if (Array.isArray(list)) {
      return list;
    }
  }

  return [];
}

export function extractPaginatedCount(
  response: unknown,
  itemsLength: number,
  page: number,
  pageSize: number
): number {
  const record = asRecord(response);
  if (!record) {
    return itemsLength;
  }

  const count =
    parsePositiveNumber(record.count) ??
    parsePositiveNumber(record.total) ??
    parsePositiveNumber(record.total_count);

  if (count !== null) {
    return count;
  }

  if (itemsLength < pageSize) {
    return (page - 1) * pageSize + itemsLength;
  }

  return page * pageSize;
}

export function buildPaginatedResult<T>(
  items: T[],
  response: unknown,
  page: number,
  pageSize: number
): PaginatedResult<T> {
  return {
    items,
    count: extractPaginatedCount(response, items.length, page, pageSize),
    page,
    pageSize,
  };
}

export function buildBillingPaginationParams(
  page: number,
  pageSize: number = BILLING_DEFAULT_PAGE_SIZE
): Record<string, string | number> {
  return {
    page,
    page_size: pageSize,
  };
}
